import { useEffect, useRef } from 'react';
import { useAppStore } from 'store/app/appStore';
import { backToGameToastService, type LastPlayedGameData } from '../services/BackToGameToastService';

/**
 * Hook that automatically triggers the back to game toast
 * Shows only once per session, 2 seconds after lobby loads
 * Only shows when openType !== 'ingame'
 */
export const useBackToGameToast = () => {
    const hasShownToast = useRef(false);
    const lastPlayedGame = useRef<LastPlayedGameData | null>(null);
    const { isVisible, settings } = useAppStore(
        state => ({
            isVisible: state.isVisible,
            settings: state.settings
        })
    );

    useEffect(() => {
        // Don't show if already shown this session
        if (hasShownToast.current) {
            return;
        }

        // Don't show if lobby is not visible
        if (!isVisible) {
            return;
        }

        // Don't show if opened from in-game (matches original logic)
        if (settings.openType === 'ingame') {
            return;
        }

        // Don't show if player ID is not available yet
        if (!settings.playerId) {
            return;
        }

        backToGameToastService.setPlayerId(settings.playerId);
        lastPlayedGame.current = backToGameToastService.getLastPlayedGame();

        // Set up delayed toast showing
        const timeoutId = setTimeout(() => {
            // Double-check conditions before showing
            if (hasShownToast.current || !isVisible) {
                return;
            }

            // Mark as shown to prevent multiple shows
            hasShownToast.current = true;

            // Show the toast using the game captured at arm-time
            backToGameToastService.showLastPlayed(lastPlayedGame.current).catch(error => {
                console.error('[useBackToGameToast] Error showing toast:', error);
            });
        }, 2000); // 2 seconds delay like original

        // Cleanup timeout on unmount or dependency change
        return () => {
            clearTimeout(timeoutId);
        };
    }, [isVisible, settings.openType, settings.playerId]);

    // Reset the flag when lobby becomes invisible (for testing/development)
    useEffect(() => {
        if (!isVisible && hasShownToast.current) {
            // Optional: reset flag when lobby becomes invisible
            // This allows re-showing the toast if lobby is reopened
            // Comment out if you want strict once-per-session behavior
            // hasShownToast.current = false;
        }
    }, [isVisible]);
};
